import * as React from 'react';
import type { ReactNode } from 'react';
import { cx } from '../utils';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeProps } from '../escape-hatches';
import type { Density } from '../density';
import { useFormDensity, useFormDensityExplicit } from '../density';

export interface FieldProps extends StyledProps, WithChildren, UnsafeProps {
  prefix?: ReactNode;
  suffix?: ReactNode;
  disabled?: boolean;
  /* Per-component density (ADR-008). When omitted, inherits from
     the nearest FormDensityContext (set by FormField). Falls back
     to 'default' when no context is present. */
  density?: Density;
  /** Visual height variant. Routes through ADR-045 `--control-h`. @default 'md' */
  size?: 'sm' | 'md' | 'lg';
  /* ADR-031 — `data-field-width` selector hook for the inline-size
     value→CSS mapping. Plumbed by `<Input>` (and by `<Input>` only
     in v5.1 per ADR-032 deferral). Field itself stays width-agnostic
     for the controls that don't opt in. */
  'data-field-width'?: 'fill' | 'content' | 'auto';
  /* ADR-031 — presence-only attribute paired with the
     `--field-inline-size` custom-property value carrier on `style`. */
  'data-field-width-numeric'?: true;
}

export function Field({
  prefix,
  suffix,
  disabled,
  className,
  children,
  density,
  size = 'md',
  unsafe_className,
  unsafe_style,
  'data-field-width': dataFieldWidth,
  'data-field-width-numeric': dataFieldWidthNumeric,
}: FieldProps) {
  const formDensity = useFormDensity();
  const formExplicit = useFormDensityExplicit();
  /* Resolution + attribute-rendering — codex audit #108 R7→R10:
     - density prop EXPLICIT (incl. 'default') → render that
     - else if FormField explicitly set density → render formDensity
     - else if direct `<FormDensityContext.Provider value="compact|comfortable">`
       use (a public, documented surface) — formExplicit will be false
       but formDensity is non-default → still treat as explicit
     - else (no Field prop, no Form explicit, context is at its
       'default' default) → omit data-density so the ambient
       JacquardProvider density rule applies. */
  const isExplicit = density !== undefined || formExplicit || formDensity !== 'default';
  const resolved = density ?? formDensity;
  return (
    <div
      className={cx(
        'j-field',
        size === 'sm' && 'j-field-sm',
        size === 'lg' && 'j-field-lg',
        className,
        unsafe_className,
      )}
      data-disabled={disabled ? 'true' : undefined}
      data-density={isExplicit ? resolved : undefined}
      data-field-width={dataFieldWidth}
      data-field-width-numeric={dataFieldWidthNumeric ? '' : undefined}
      style={unsafe_style}
    >
      {prefix && <span className="j-field-prefix">{prefix}</span>}
      {children}
      {suffix && <span className="j-field-suffix">{suffix}</span>}
    </div>
  );
}
